import React, { useMemo } from 'react'
import { useWorkspace } from '../../WorkspaceContext'
import { useDriveView } from '../../DriveViewContext'
import type { DriveSortField, DriveTypeFilter } from '../../DriveViewContext'
import { isMediaFile } from '../../../../shared/utils/isMediaFile'
import '../sidebar.scss'
import styles from './FileListTab.module.scss'
import SortIcon from '../../../../shared/components/svgIcons/SortIcon'

const typeFilters: { value: DriveTypeFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'folders', label: 'Folders' },
    { value: 'documents', label: 'Documents' },
    { value: 'code', label: 'Code' },
    { value: 'text', label: 'Text' },
    { value: 'media', label: 'Media' },
]

const FileListTab = () => {
    const { Files, Snippets, Folders } = useWorkspace();
    const { typeFilter, setTypeFilter, sortField, setSortField, sortDirection, setSortDirection, typeCounts } = useDriveView();

    const totals = useMemo(() => {
        const mediaCount = Files.filter(isMediaFile).length;

        return {
            all: Files.length + Snippets.length + Folders.length,
            folders: Folders.length,
            files: Files.length,
            snippets: Snippets.length,
            media: mediaCount,
        };
    }, [Files, Snippets, Folders]);

    return (
        <div className='sb-tab-flx-cont'>
            <div className={`${styles.fileListTab} sb-mode-c`}>
                <div id='filelist-topbar'>
                    <div className='filelist-title'>
                        <h3><span>Flash</span> Drive</h3>
                        <span>{totals.all} items</span>
                    </div>
                </div>


                <div className={styles.fltToolbar}>
                    <div className={styles.fltSort}>
                        <span className={styles.fltSortIcon} aria-hidden='true'>
                            <SortIcon />
                        </span>
                        <select
                            aria-label='Sort files'
                            value={sortField}
                            onChange={(e) => setSortField(e.target.value as DriveSortField)}

                            style={{ cursor: 'pointer' }}
                        >
                            <option value='name'>Name</option>
                            <option value='date'>Date</option>
                            <option value='size'>Size</option>
                        </select>
                        <button
                            type='button'
                            className={styles.fltSortDir}
                            aria-label={sortDirection === 'asc' ? 'Sort descending' : 'Sort ascending'}
                            title={sortDirection === 'asc' ? 'Sort descending' : 'Sort ascending'}
                            onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
                        >
                            <svg
                                className={sortDirection === 'desc' ? styles.isDesc : ''}
                                viewBox='0 0 24 24'
                                aria-hidden='true'
                            >
                                <path d='M12 5v14' />
                                <path d='M6 11l6-6 6 6' />
                            </svg>
                        </button>
                    </div>
                </div>

                <div className={styles.fltSection}>
                    <span className={styles.fltLabel}>Type</span>
                    <div className={styles.fltChips}>
                        {typeFilters.map(filter => {
                            const disabled = filter.value !== 'all' && typeCounts[filter.value] === 0;

                            return (
                                <button
                                    key={filter.value}
                                    type='button'
                                    className={`${styles.fltChip} ${typeFilter === filter.value ? styles.active : ''}`}
                                    disabled={disabled}
                                    title={disabled ? `No ${filter.label.toLowerCase()} here yet` : undefined}
                                    onClick={() => setTypeFilter(filter.value)}
                                >
                                    {filter.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className={styles.fltSummary}>
                    <div>
                        <strong>{totals.folders}</strong>
                        <span>Folders</span>
                    </div>
                    <div>
                        <strong>{totals.files}</strong>
                        <span>Uploads</span>
                    </div>
                    <div>
                        <strong>{totals.snippets}</strong>
                        <span>Snippets</span>
                    </div>
                    <div>
                        <strong>{totals.media}</strong>
                        <span>Media</span>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default FileListTab
